import React, { useEffect, useMemo, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import {
  WSButton,
  WSElement,
  WSFlexBox,
  WSLoader,
  WSText
} from "@wingspanhq/fe-component-library";
import { OAuthAuthorizationResult } from "@wingspanhq/wingspan-api-sdk";
import { useSession } from "@hooks/useSession";
import { ErrorAlert } from "@components/ErrorAlert";
import { useBrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import { LayoutFullscreen } from "../../../shared/components/LayoutFullscreen";
import { AuthorizeConsent } from "../components/AuthorizeConsent";
import { RedirectConfirmation } from "../components/RedirectConfirmation";
import {
  useMutationOAuthAuthorizeDecision,
  useQueryOAuthAuthorizeContext
} from "../hooks/useOAuthAuthorize";
import { useQueryCandidateAccounts } from "../hooks/useQueryCandidateAccounts";
import styles from "./RouteAuthorize.module.scss";

/** A terminal result that sends the browser somewhere. */
type RedirectResult = Extract<
  OAuthAuthorizationResult,
  { redirectUri: string }
>;

const isRedirect = (
  result: OAuthAuthorizationResult
): result is RedirectResult => result.disposition !== "DirectError";

/**
 * Builds the URL the browser should actually be sent to.
 *
 * The provider returns the *registered* `redirectUri` and the authorization
 * parameters as separate fields (see OAuthAuthorizationRedirectSuccess /
 * RedirectError), so assembling them is the caller's job — navigating to
 * `redirectUri` alone strands the authorization code here and the client sees
 * a bare callback with no `code`, `state` or `iss`.
 *
 * Success and error redirects share `disposition: "Redirect"`, so the branch
 * is on which payload is present, not on the discriminator. Existing query
 * parameters on the registered URI are preserved.
 */
const buildRedirectTarget = (result: RedirectResult): string => {
  const target = new URL(result.redirectUri);
  if ("code" in result && result.code) {
    target.searchParams.set("code", result.code);
  } else if ("error" in result && result.error) {
    target.searchParams.set("error", result.error);
    if (result.errorDescription) {
      // RFC 6749 spells this snake_case on the wire; the JSON field is camel.
      target.searchParams.set("error_description", result.errorDescription);
    }
  }
  if (result.state) {
    target.searchParams.set("state", result.state);
  }
  if (result.iss) {
    target.searchParams.set("iss", result.iss);
  }
  return target.toString();
};

const ErrorScreen: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => {
  const history = useHistory();

  return (
    <LayoutFullscreen whiteBackground>
      <WSElement className={styles.errorContainer}>
        <WSText.Heading2 mb="M" className={styles.errorTitle}>
          {title}
        </WSText.Heading2>
        {children}
        <WSElement mt="L">
          <WSButton.Secondary onClick={() => history.push("/member/dashboard")}>
            Go to Dashboard
          </WSButton.Secondary>
        </WSElement>
      </WSElement>
    </LayoutFullscreen>
  );
};

export const RouteAuthorize: React.FC = () => {
  useBrowserPageTitle("Authorize Application");
  const location = useLocation();
  const { personId } = useSession();

  // The V3 authorize endpoint hands the consent screen an opaque interaction
  // reference and nothing else — client, scopes, redirect and PKCE all stay
  // server-side against the interaction record, so they are never re-supplied
  // (or forgeable) here.
  const interactionId = useMemo(
    () => new URLSearchParams(location.search).get("interactionId") ?? "",
    [location.search]
  );

  const [selectedIndex, setSelectedIndex] = useState<number | undefined>();
  const [pendingRedirect, setPendingRedirect] = useState<RedirectResult>();

  const queryContext = useQueryOAuthAuthorizeContext(interactionId);
  const [decide, decideMeta] = useMutationOAuthAuthorizeDecision();

  const candidates = queryContext.data?.candidates ?? [];
  const { accountsById } = useQueryCandidateAccounts(
    candidates.map(candidate => candidate.accountId)
  );

  // A single candidate is not a choice — preselect it so the card reads as
  // context rather than an unanswered question.
  useEffect(() => {
    if (candidates.length === 1) {
      setSelectedIndex(0);
    }
  }, [candidates.length]);

  const submit = (approved: boolean) => {
    const context = queryContext.data;
    if (!context) return;
    const selected =
      selectedIndex === undefined ? undefined : candidates[selectedIndex];

    decide(
      {
        oAuthAuthorizationDecision: {
          interactionId: context.interactionId,
          decisionNonce: context.decisionNonce,
          approved,
          ...(selected && {
            accountId: selected.accountId,
            roleScope: selected.roleScope
          })
        }
      },
      {
        onSuccess: (result: OAuthAuthorizationResult) => {
          if (!isRedirect(result)) return;
          if (result.requiresDestinationConfirmation) {
            setPendingRedirect(result);
            return;
          }
          window.location.href = buildRedirectTarget(result);
        }
      }
    );
  };

  if (!interactionId) {
    return (
      <ErrorScreen title="Invalid request">
        <WSText.Paragraph>
          This authorization link is missing its interaction reference. Start
          again from the application you were connecting.
        </WSText.Paragraph>
      </ErrorScreen>
    );
  }

  // Consent needs a direct person session. Preserve the full path in the hash
  // so `interactionId` survives the round trip, matching the convention in
  // src/modules/PaymentApp/routes/protected/index.tsx.
  if (!personId) {
    return (
      <Redirect to={`/member/sign-in#${location.pathname}${location.search}`} />
    );
  }

  if (pendingRedirect) {
    return (
      <LayoutFullscreen whiteBackground>
        <RedirectConfirmation
          redirectUri={pendingRedirect.redirectUri}
          onContinue={() => {
            window.location.href = buildRedirectTarget(pendingRedirect);
          }}
          onCancel={() => setPendingRedirect(undefined)}
        />
      </LayoutFullscreen>
    );
  }

  // A DirectError has no registered redirect to fail towards — the only safe
  // place to show it is here.
  const result = decideMeta.data;
  if (result && !isRedirect(result)) {
    return (
      <ErrorScreen title="Authorization failed">
        <WSText.Paragraph>
          {result.errorDescription || result.error}
        </WSText.Paragraph>
      </ErrorScreen>
    );
  }

  if (queryContext.isLoading) {
    return (
      <LayoutFullscreen whiteBackground>
        <WSFlexBox.Center>
          <WSFlexBox direction="column" gap="M">
            <WSLoader.Spinner size="M" />
            <WSText.Paragraph>
              Loading authorization details...
            </WSText.Paragraph>
          </WSFlexBox>
        </WSFlexBox.Center>
      </LayoutFullscreen>
    );
  }

  if (queryContext.error || !queryContext.data) {
    return (
      <ErrorScreen title="Authorization error">
        <ErrorAlert error={queryContext.error} mb="L" context="Other" />
      </ErrorScreen>
    );
  }

  if (candidates.length === 0) {
    return (
      <ErrorScreen title="No eligible accounts">
        <WSText.Paragraph>
          None of your accounts can grant the access this application asked for.
          Ask an account admin to grant you the matching role, then try again.
        </WSText.Paragraph>
      </ErrorScreen>
    );
  }

  return (
    <LayoutFullscreen whiteBackground>
      {decideMeta.error && (
        <WSElement mb="M" className={styles.errorMessageWrapper}>
          <ErrorAlert error={decideMeta.error} context="Other" />
        </WSElement>
      )}
      <AuthorizeConsent
        context={queryContext.data}
        accountsById={accountsById}
        selectedIndex={selectedIndex}
        onSelectCandidate={setSelectedIndex}
        onApprove={() => submit(true)}
        onDeny={() => submit(false)}
        isLoading={decideMeta.isLoading}
      />
    </LayoutFullscreen>
  );
};
